import type { SearchResultItem, SearchWindowRow } from "../types";

// // // //

// Max number of results shown in each category group
export const MAX_RESULTS_PER_GROUP = 50;

// // // //

/**
 * Flattens the SearchWindow sections (recent searches, top results, category groups)
 * into a single list of header + option rows for virtualized rendering.
 * Empty sections are omitted.
 */
export function getSearchWindowRows(props: {
    recentSearches: SearchResultItem[];
    topResults: SearchResultItem[];
    groups: Record<string, SearchResultItem[]>;
}): SearchWindowRow[] {
    const { recentSearches, topResults, groups } = props;
    const rows: SearchWindowRow[] = [];

    // Adds a header row followed by an option row for each item
    const addSection = (section: {
        label: string;
        items: SearchResultItem[];
        isRecentSearches?: boolean;
        showCategory?: boolean;
    }) => {
        const {
            label,
            items,
            isRecentSearches = false,
            showCategory = false,
        } = section;

        if (items.length === 0) {
            return;
        }

        rows.push({
            type: "header",
            label,
            isFirst: rows.length === 0,
            isRecentSearches,
        });

        for (const item of items) {
            rows.push({
                type: "option",
                item,
                showCategory,
                altPrefix: isRecentSearches
                    ? `${item.content.label} Recent`
                    : undefined,
            });
        }
    };

    addSection({
        label: "Recent Searches",
        items: recentSearches,
        isRecentSearches: true,
    });

    addSection({
        label: topResults.length === 1 ? "1 result" : "Top results",
        items: topResults,
        showCategory: true,
    });

    // Top results are not repeated inside of their category group
    const topHrefs = new Set(topResults.map((item) => item.href));
    for (const [category, results] of Object.entries(groups)) {
        addSection({
            label: category,
            items: results
                .filter((item) => !topHrefs.has(item.href))
                .slice(0, MAX_RESULTS_PER_GROUP),
        });
    }

    return rows;
}

/**
 * Header rows are not selectable, and are skipped by keyboard navigation
 */
export function isSearchWindowHeaderRow(row: SearchWindowRow): boolean {
    return row.type === "header";
}
